import { createFileRoute } from '@tanstack/react-router';
import { EvaluationTypeIcon } from '@/app/components/features/evaluations/EvaluationTypeIcon';
import { getTenderPackageDetailsQuery } from '@/app/hooks/queries/useTenderPackages';
import {
  Breadcrumb,
  BreadcrumbMenu,
  BreadcrumbMenuItem,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/app/ui/components/breadcrumb';
import {
  Header,
  HeaderSidechatButton,
  HeaderBackButton,
  HeaderSidebarTrigger,
} from '@/app/ui/components/header';
import { RunAnalysisHeaderButton } from '@/app/components/features/evaluations/RunAnalysisButton';
import {
  ClauseScannerAnalysisType,
  DocumentTypes,
  EvaluationTypeLabelsEnum,
} from '@/app/utils/constants';
import { Refresh01Icon, SearchFocusIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useTranslations } from 'use-intl';
import { ClausesPageContent } from '@/app/components/features/evaluations/Clauses/ClausesPageContent';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';
import { useSetAtom } from 'jotai';
import { rerunClauseAnalysisOpenAtom } from '@/app/components/features/evaluations/Clauses/store';
import { useSuspenseQuery } from '@tanstack/react-query';

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/tender-packages/$tenderPackageId/_layout/(main)/clauses/',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslations('pages.tenderHome');
  const tGeneral = useTranslations('general');
  const tFeature = useTranslations('features.clauses');
  const { tenderPackageId, projectId } = Route.useParams();
  const { data: evaluation } = useSuspenseQuery(getTenderPackageDetailsQuery(tenderPackageId));
  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));
  const setRerunAnalysisOpen = useSetAtom(rerunClauseAnalysisOpenAtom);

  return (
    <>
      <Header>
        <HeaderBackButton
          to="/project/$projectId/tender-packages/$tenderPackageId"
          params={{ projectId, tenderPackageId }}
        />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbMenu>
              <BreadcrumbMenuItem to="/project/$projectId" params={{ projectId }}>
                <ProjectTypeIcon type={project.type} />
                {project.name}
              </BreadcrumbMenuItem>
            </BreadcrumbMenu>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/project/$projectId/tender-packages/$tenderPackageId"
                params={{ projectId, tenderPackageId }}
              >
                <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.TENDER_PACKAGE} />
                <span className="line-clamp-1">{evaluation.name}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem className="max-md:hidden">
              <BreadcrumbLink
                to="/project/$projectId/tender-packages/$tenderPackageId"
                params={{ projectId, tenderPackageId }}
              >
                <HugeiconsIcon icon={SearchFocusIcon} />
                {t('title')}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbPage>{tFeature('title')}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <HeaderSidechatButton />
        <RunAnalysisHeaderButton
          entityId={tenderPackageId}
          documentType={DocumentTypes.TENDER_PACKAGE}
          onClick={() => setRerunAnalysisOpen(true)}
          aria-label={tGeneral('reRunAnalysis')}
        >
          <HugeiconsIcon icon={Refresh01Icon} />
        </RunAnalysisHeaderButton>
      </Header>
      <ClausesPageContent
        clauseScannerType={ClauseScannerAnalysisType.TENDER_PACKAGE}
        resourceId={tenderPackageId}
      />
    </>
  );
}
